import { useState, useCallback } from 'react';
import { logger } from '../../../shared/utils/logger';

interface FileUploadState {
  selectedFiles: File[];
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
}

export const useFileUpload = () => {
  const [state, setState] = useState<FileUploadState>({
    selectedFiles: [],
    isUploading: false,
    uploadProgress: 0,
    error: null
  });

  /**
   * Проверка возможности предпросмотра файла
   */
  const canPreviewFile = useCallback((file: File): boolean => {
    const previewableTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    return previewableTypes.includes(file.type);
  }, []);

  /**
   * Проверка на изображение
   */
  const isImageFile = useCallback((file: File): boolean => {
    return file.type.startsWith('image/');
  }, []);

  /**
   * Проверка на PDF
   */
  const isPdfFile = useCallback((file: File): boolean => {
    return file.type === 'application/pdf';
  }, []);

  /**
   * Получение иконки файла
   */
  const getFileIcon = useCallback((file: File): string => {
    if (isImageFile(file)) return '🖼️';
    if (isPdfFile(file)) return '📄';
    if (file.type.includes('video')) return '🎥';
    if (file.type.includes('audio')) return '🎵';
    if (file.type.includes('text')) return '📝';
    if (file.type.includes('zip') || file.type.includes('rar')) return '📦';
    if (file.type.includes('word')) return '📘';
    if (file.type.includes('excel') || file.type.includes('spreadsheet')) return '📊';
    if (file.type.includes('powerpoint') || file.type.includes('presentation')) return '📈';
    return '📎';
  }, [isImageFile, isPdfFile]);

  /**
   * Форматирование размера файла
   */
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  /**
   * Валидация файла
   */
  const validateFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    // Максимальный размер файла - 25MB
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      return { isValid: false, error: `Файл слишком большой. Максимальный размер: 25MB` };
    }

    // Проверяем расширение файла
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'application/zip',
      'application/x-rar-compressed'
    ];

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: `Неподдерживаемый тип файла: ${file.type}` };
    }

    return { isValid: true };
  }, []);

  /**
   * Добавление файлов
   */
  const addFiles = useCallback((newFiles: File[]) => {
    const validFiles: File[] = [];
    let errorMessage = '';

    for (const file of newFiles) {
      const validation = validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        errorMessage = validation.error || 'Ошибка валидации файла';
        break;
      }
    }

    // Проверяем общее количество файлов (максимум 25)
    const totalFiles = state.selectedFiles.length + validFiles.length;
    if (totalFiles > 25) {
      errorMessage = 'Максимальное количество файлов: 25';
      return;
    }

    setState(prev => ({
      ...prev,
      selectedFiles: [...prev.selectedFiles, ...validFiles],
      error: errorMessage || null
    }));

    logger.debug(`📎 Добавлено ${validFiles.length} файлов`);
  }, [state.selectedFiles, validateFile]);

  /**
   * Удаление файла по индексу
   */
  const removeFile = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== index),
      error: null
    }));

    logger.debug(`🗑️ Удален файл с индексом ${index}`);
  }, []);

  /**
   * Очистка всех файлов
   */
  const clearFiles = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedFiles: [],
      error: null
    }));

    logger.debug('🧹 Все файлы очищены');
  }, []);

  /**
   * Обработка выбора файлов из input
   */
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
    // Очищаем input для возможности повторного выбора того же файла
    event.target.value = '';
  }, [addFiles]);

  /**
   * Обработка drag & drop
   */
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      addFiles(files);
    }
  }, [addFiles]);

  /**
   * Обработка вставки из буфера обмена
   */
  const handlePaste = useCallback((event: ClipboardEvent) => {
    const items = Array.from(event.clipboardData?.items || []);
    const files = items
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (files.length > 0) {
      addFiles(files);
    }
  }, [addFiles]);

  /**
   * Создание превью для изображения
   */
  const createImagePreview = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!isImageFile(file)) {
        reject(new Error('Файл не является изображением'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = () => {
        reject(new Error('Ошибка чтения файла'));
      };
      reader.readAsDataURL(file);
    });
  }, [isImageFile]);

  return {
    selectedFiles: state.selectedFiles,
    isUploading: state.isUploading,
    uploadProgress: state.uploadProgress,
    error: state.error,
    
    // Утилиты
    canPreviewFile,
    isImageFile,
    isPdfFile,
    getFileIcon,
    formatFileSize,
    validateFile,
    createImagePreview,
    
    // Методы
    addFiles,
    removeFile,
    clearFiles,
    handleFileSelect,
    handleDrop,
    handlePaste
  };
};
